// AI LISTE ANALIZI — tedarikci urununun Etsy'de nasil listelenecegi.
// Guclu model urunun tum fotograflarina, aciklamasina ve eksenlerine bakar:
// kisisellestirme alanlari (eksik olanlar dahil), gereken chart'lar, aliciya
// talimat. Kullanici inceler: onaylar ya da duzeltme ister. Duzeltmeden cikan
// genel kural hatirlanir ve sonraki her analize uygulanir (ogrenen sistem).

import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle, BookOpen, Check, ChevronDown, ChevronRight, Loader2, Pencil, Plus, RefreshCw, Sparkles, Trash2, Wand2, X,
} from "lucide-react";
import type { ReactNode } from "react";
import type { ListingAnalysisField, ListingAnalysisChart, ProductCategory } from "@erc/shared";
import { authHeaders, getBackendUrl } from "../../lib/http";
import { useOptionSets } from "./ProductOptionsEditor";

interface Lesson {
  id: string; text: string; appliesTo: string | null; source: string; active: boolean;
  useCount: number; originTitle: string | null; createdAt: string;
}
interface LessonsResponse {
  data: Lesson[]; systemRules: string[];
  models: Array<{ id: string; label: string; provider: "anthropic" | "openrouter" }>; defaultModel: string;
}

async function call<T>(path: string, method: string, body?: unknown): Promise<T> {
  const r = await fetch(`${getBackendUrl()}${path}`, { method, headers: authHeaders(), body: body === undefined ? undefined : JSON.stringify(body) });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || `İstek başarısız (${r.status})`);
  return j as T;
}

export function useListingLessons() {
  return useQuery({
    queryKey: ["listing-lessons"],
    staleTime: 30_000,
    queryFn: () => call<LessonsResponse>("/api/listing-lessons", "GET"),
  });
}


export function ListingAnalysisPanel({ catalogId, cat }: { catalogId: string; cat: ProductCategory }) {
  const qc = useQueryClient();
  const lessonsQ = useListingLessons();
  const { data: optionSets = [] } = useOptionSets();
  const setName = (id?: string) => optionSets.find((s) => s.id === id)?.name;
  const analysis = cat.listingAnalysis;
  const ready = !!analysis && (analysis.status === "draft" || analysis.status === "approved");
  const images = cat.source?.images ?? [];

  // Son secilen model hatirlanir (tarayici)
  const [model, setModelState] = useState(() => { try { return localStorage.getItem("erc-listing-analysis-model") ?? ""; } catch { return ""; } });
  const setModel = (m: string) => { setModelState(m); try { localStorage.setItem("erc-listing-analysis-model", m); } catch { /* yok say */ } };
  const modelIds = (lessonsQ.data?.models ?? []).map((m) => m.id);
  const effectiveModel = model && modelIds.includes(model) ? model : lessonsQ.data?.defaultModel ?? "";
  const [busy, setBusy] = useState<null | "analyze" | "revise" | "save" | "apply">(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [feedback, setFeedback] = useState("");
  const [showFeedback, setShowFeedback] = useState(false);
  const [learned, setLearned] = useState<Array<{ id: string; text: string; appliesTo: string | null }>>([]);
  // Elle duzenleme (alan silme, talimat) — onay ya da kayitla yazilir
  const [fields, setFields] = useState<ListingAnalysisField[]>([]);
  // Chart secimi (kutuphaneden / tedarikci fotografindan) kullanici degistirebilir
  const [charts, setCharts] = useState<ListingAnalysisChart[]>([]);
  const [instructions, setInstructions] = useState({ en: "", exampleEn: "", tr: "" });
  const [dirty, setDirty] = useState(false);
  const [pickedFields, setPickedFields] = useState<Set<string>>(new Set());
  const [pickedCharts, setPickedCharts] = useState<Set<number>>(new Set());
  // "Secenek kutuphanesine ekle" isaretli alanlar — yoksa yeni setler urune ozel
  const [libraryKeys, setLibraryKeys] = useState<Set<string>>(new Set());
  const updateField = (key: string, patch: Partial<ListingAnalysisField>) => {
    setFields((cur) => cur.map((x) => (x.key === key ? { ...x, ...patch } : x)));
    setDirty(true);
  };


  useEffect(() => {
    if (!ready || !analysis) return;
    setFields(analysis.fields);
    setCharts(analysis.charts);
    setInstructions(analysis.buyerInstructions);
    setDirty(false);
    // Uygulama secimi: eklentide kaydedilen secim; yoksa eklenmesi gereken alanlar
    // ve kaynagi olan chart'lar
    setPickedFields(new Set(analysis.pickedFields ?? analysis.fields.map((f) => f.key)));
    setPickedCharts(new Set(analysis.pickedCharts ?? analysis.charts.map((c, i) => (c.status === "in_library" || c.status === "in_supplier_images" ? i : -1)).filter((i) => i >= 0)));
  }, [ready, analysis?.analyzedAt, analysis?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  // Arka plan analizi (ice aktarimda baslatilan) bitene kadar yokla
  useEffect(() => {
    if (analysis?.status !== "running") return;
    const t = setInterval(() => qc.invalidateQueries({ queryKey: ["supplier-catalogs"] }), 15_000);
    return () => clearInterval(t);
  }, [analysis?.status, qc]);

  const refresh = () => Promise.all([
    qc.invalidateQueries({ queryKey: ["supplier-catalogs"] }),
    qc.invalidateQueries({ queryKey: ["supplier-catalog"] }),
  ]);
  const base = `/api/supplier-catalogs/${catalogId}/categories/${encodeURIComponent(cat.id)}/listing-analysis`;

  async function run(withFeedback?: string) {
    setBusy(withFeedback ? "revise" : "analyze"); setError(null); setNotice(null);
    try {
      const r = await call<{ learned: typeof learned }>(base, "POST", {
        model: effectiveModel,
        ...(withFeedback ? { feedback: withFeedback } : {}),
      });
      setLearned(r.learned ?? []);
      if (withFeedback) { setFeedback(""); setShowFeedback(false); }
      await Promise.all([refresh(), qc.invalidateQueries({ queryKey: ["listing-lessons"] })]);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  async function save(status?: "approved" | "draft") {
    setBusy("save"); setError(null);
    try {
      await call(base, "PATCH", { ...(dirty ? { fields, charts, buyerInstructions: instructions } : {}), ...(status ? { status } : {}) });
      setDirty(false);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  async function apply() {
    setBusy("apply"); setError(null); setNotice(null);
    try {
      if (dirty) await call(base, "PATCH", { fields, charts, buyerInstructions: instructions });
      const r = await call<{ result: { created: string[]; attached: string[]; skipped: string[] } }>(`${base}/apply`, "POST", {
        fieldKeys: [...pickedFields], chartIndexes: [...pickedCharts], libraryKeys: [...libraryKeys],
      });
      const { created, attached, skipped } = r.result;
      setNotice([
        attached.length ? `Bağlandı: ${attached.join(", ")}` : "Bağlanan yeni seçenek yok",
        created.length ? `Kütüphanede açıldı: ${created.join(", ")}` : "",
        skipped.length ? `Atlandı: ${skipped.join(", ")}` : "",
      ].filter(Boolean).join(" · "));
      setDirty(false);
      await Promise.all([refresh(), qc.invalidateQueries({ queryKey: ["option-sets"] })]);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  const lessonById = useMemo(() => new Map((lessonsQ.data?.data ?? []).map((l) => [l.id, l])), [lessonsQ.data]);
  const toggle = <T,>(set: Set<T>, v: T) => { const n = new Set(set); if (n.has(v)) n.delete(v); else n.add(v); return n; };

  return (
    <div className="rounded-xl border border-violet-200 bg-violet-50/30 p-3">
      {/* Baslik + calistir */}
      <div className="flex flex-wrap items-center gap-2">
        <Sparkles className="h-4 w-4 text-violet-600" />
        <span className="font-semibold text-gray-900">AI liste analizi</span>
        {analysis?.status === "approved" && <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-700">Onaylı</span>}
        {analysis?.status === "draft" && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-700">İnceleme bekliyor</span>}
        {analysis?.status === "running" && <span className="flex items-center gap-1 rounded-full bg-violet-100 px-2 py-0.5 text-[11px] font-medium text-violet-700"><Loader2 className="h-3 w-3 animate-spin" />Arka planda sürüyor</span>}
        {analysis?.status === "failed" && <span className="rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-medium text-red-700">Başarısız</span>}
        <div className="ml-auto flex items-center gap-1.5">
          <select value={effectiveModel} onChange={(e) => setModel(e.target.value)}
            className="rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs">
            {([["anthropic", "Claude API (doğrudan)"], ["openrouter", "OpenRouter"]] as const).map(([provider, label]) => (
              <optgroup key={provider} label={label}>
                {(lessonsQ.data?.models ?? []).filter((m) => m.provider === provider).map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
              </optgroup>
            ))}
          </select>
          <button onClick={() => run()} disabled={!!busy}
            className="flex items-center gap-1.5 rounded-lg bg-violet-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-violet-700 disabled:opacity-50">
            {busy === "analyze" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : ready ? <RefreshCw className="h-3.5 w-3.5" /> : <Sparkles className="h-3.5 w-3.5" />}
            {ready ? "Yeniden analiz" : "Analiz et"}
          </button>
        </div>
      </div>
      {busy === "analyze" || busy === "revise" ? (
        <p className="mt-2 text-xs text-violet-700">Model tüm fotoğrafları ve açıklamayı inceliyor — 1-3 dakika sürebilir.</p>
      ) : !analysis ? (
        <p className="mt-2 text-xs text-gray-500">
          Ürünün tüm fotoğrafları, açıklaması ve varyasyonları incelenir; kişiselleştirme alanları (eksik olanlar dahil),
          gereken chart'lar ve alıcı talimatı çıkarılır. Öğrenilmiş kurallarına göre çalışır.
        </p>
      ) : null}
      {analysis?.status === "failed" && analysis.error && <p className="mt-2 text-xs text-red-600">{analysis.error}</p>}
      {error && <p className="mt-2 text-xs text-red-600">{error}</p>}

      {learned.length > 0 && (
        <div className="mt-2 rounded-lg border border-emerald-200 bg-emerald-50 p-2 text-xs text-emerald-800">
          <p className="font-semibold">Öğrenildi — sonraki analizlerde uygulanacak:</p>
          <ul className="mt-1 list-disc pl-4">{learned.map((l) => <li key={l.id}>{l.text}{l.appliesTo ? ` (${l.appliesTo})` : ""}</li>)}</ul>
        </div>
      )}

      {ready && analysis && (
        <div className="mt-3 space-y-3">
          <div>
            {analysis.productKindTr && <p className="text-xs font-semibold uppercase tracking-wide text-violet-700">{analysis.productKindTr}</p>}
            <p className="text-gray-800">{analysis.summaryTr}</p>
          </div>

          {analysis.warningsTr.length > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
              {analysis.warningsTr.map((w, i) => <p key={i} className="flex gap-1.5"><AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />{w}</p>)}
            </div>
          )}

          <EtsyListingView
            cat={cat} analysis={analysis} fields={fields} images={images} setName={setName}
            setChart={(id) => { const u = optionSets.find((x) => x.id === id)?.chartImageUrl; return u ? (/^(https?:|data:)/.test(u) ? u : `${getBackendUrl()}${u}`) : undefined; }}
            updateField={updateField}
            removeField={(key) => { setFields(fields.filter((x) => x.key !== key)); setDirty(true); }}
            pickedFields={pickedFields} togglePicked={(key) => setPickedFields(toggle(pickedFields, key))}
            pickedCharts={pickedCharts} toggleChart={(i) => setPickedCharts(toggle(pickedCharts, i))}
            libraryKeys={libraryKeys} toggleLibrary={(key) => setLibraryKeys(toggle(libraryKeys, key))}
            charts={charts}
            libraryCharts={optionSets.filter((o) => o.chartImageUrl && o.inLibrary !== false).map((o) => ({ id: o.id, name: o.name, url: /^(https?:|data:)/.test(o.chartImageUrl!) ? o.chartImageUrl! : `${getBackendUrl()}${o.chartImageUrl}` }))}
            onPickChart={(i, sel) => {
              setCharts((cur) => cur.map((c, j) => (j !== i ? c : sel.kind === "library"
                ? { ...c, status: "in_library" as const, librarySetId: sel.id, imageIndexes: undefined }
                : { ...c, status: "in_supplier_images" as const, imageIndexes: [sel.index], librarySetId: undefined })));
              setPickedCharts((cur) => new Set([...cur, i]));
              setDirty(true);
            }}
            setInLibrary={(id) => { const x = optionSets.find((o) => o.id === id); return x ? x.inLibrary !== false : undefined; }}
          />

          {analysis.appliedLessonIds.length > 0 && (
            <div className="text-[11px] text-gray-500">
              <span className="font-semibold">Uygulanan kurallar: </span>
              {analysis.appliedLessonIds.map((id) => lessonById.get(id)?.text).filter(Boolean).map((t, i) => (
                <span key={i} className="mr-1 inline-block rounded bg-violet-100 px-1.5 py-0.5 text-violet-800" title={t}>
                  {t!.length > 60 ? `${t!.slice(0, 60)}…` : t}
                </span>
              ))}
            </div>
          )}

          {analysis.feedback && analysis.feedback.length > 0 && (
            <p className="text-[11px] text-gray-500">Düzeltme geçmişi: {analysis.feedback.map((f) => `“${f.text}”`).join(" · ")}</p>
          )}

          {/* Islemler */}
          <div className="flex flex-wrap items-center gap-2 border-t border-violet-100 pt-3">
            {analysis.status !== "approved" ? (
              <button onClick={() => save("approved")} disabled={!!busy}
                className="flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50">
                {busy === "save" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}Onayla
              </button>
            ) : (
              <button onClick={() => save("draft")} disabled={!!busy}
                className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50">Onayı kaldır</button>
            )}
            {dirty && (
              <button onClick={() => save()} disabled={!!busy}
                className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50">Değişiklikleri kaydet</button>
            )}
            <button onClick={() => setShowFeedback(!showFeedback)} disabled={!!busy}
              className="flex items-center gap-1.5 rounded-lg border border-violet-300 bg-white px-3 py-1.5 text-xs font-medium text-violet-700 hover:bg-violet-50 disabled:opacity-50">
              <Pencil className="h-3.5 w-3.5" />Düzeltme iste
            </button>
            <button onClick={apply} disabled={!!busy || (pickedFields.size === 0 && pickedCharts.size === 0)}
              title="Seçili alanları ve chart'ları ürünün seçeneklerine bağlar (gerekirse kütüphanede set açar)"
              className="ml-auto flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
              {busy === "apply" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />}Seçeneklere uygula
            </button>
          </div>
          {notice && <p className="text-xs text-indigo-700">{notice}</p>}
          {showFeedback && (
            <div className="rounded-lg border border-violet-200 bg-white p-2">
              <textarea value={feedback} onChange={(e) => setFeedback(e.target.value)} rows={3} autoFocus
                placeholder="Neyi yanlış yaptı? Ör: Bu bar kolye plaka üzerine kazıma — Font2 chart'ı kullanılmalı, font seçenekleri yalnız 1-4-5-6-7-8-10-12-13."
                className="w-full rounded border border-gray-200 p-2 text-xs" />
              <div className="mt-1.5 flex items-center gap-2">
                <p className="text-[11px] text-gray-500">Analiz düzeltilir; genel bir kural varsa hatırlanır ve sonraki ürünlerde uygulanır.</p>
                <button onClick={() => run(feedback)} disabled={!feedback.trim() || !!busy}
                  className="ml-auto flex items-center gap-1.5 rounded-lg bg-violet-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-violet-700 disabled:opacity-50">
                  {busy === "revise" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}Düzelt ve öğren
                </button>
              </div>
            </div>
          )}
          <p className="text-[10px] text-gray-400">{analysis.model} · {new Date(analysis.analyzedAt).toLocaleString("tr-TR")}</p>
        </div>
      )}

      <LessonsManager />
    </div>
  );
}

// ---- Etsy listeleme gorunumu ----
// Analiz, Etsy'nin urun listeleme ekranindaki bolumlerle gosterilir:
// Fotograflar, Varyasyonlar, Kisisellestirme ve alicinin urun sayfasinda
// gorecegi secim alanlari. Etsy'de gorunen her metin (Ingilizce) yerinde duzenlenir.

const ETSY_INPUT = "w-full rounded-lg border border-[#c7c7c7] bg-white px-3 py-2 text-sm text-[#222] focus:border-[#222] focus:outline-none";

function axisValuesOf(cat: ProductCategory, name?: string): string[] {
  return ((cat.source?.variationTypes ?? []) as Array<{ name: string; values: string[] }>).find((a) => a.name === name)?.values ?? [];
}

function EtsySection({ title, subtitle, children }: { title: string; subtitle?: string; children: ReactNode }) {
  return (
    <section className="rounded-xl border border-[#e1e3df] bg-white p-4">
      <h4 className="text-lg font-bold text-[#222]">{title}</h4>
      {subtitle && <p className="mb-3 text-[13px] text-[#595959]">{subtitle}</p>}
      {children}
    </section>
  );
}

function EtsyBadge({ tone, children }: { tone: "warn" | "ok" | "info" | "price"; children: ReactNode }) {
  const cls = { warn: "bg-[#fdf0d5] text-[#8a5a00]", ok: "bg-[#e6f4ea] text-[#1e7d34]", info: "bg-[#eef1fb] text-[#3b4ba8]", price: "bg-[#fde7dc] text-[#b4441b]" }[tone];
  return <span className={`whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold ${cls}`}>{children}</span>;
}

function EtsyInput({ label, value, max, onChange, multiline, hint, placeholder }: {
  label: string; value: string; max?: number; onChange: (v: string) => void;
  multiline?: boolean; hint?: string; placeholder?: string;
}) {
  return (
    <label className="mb-3 block">
      <span className="flex items-baseline justify-between gap-2">
        <span className="text-sm font-semibold text-[#222]">{label}</span>
        {max != null && <span className={`text-xs ${value.length > max ? "text-red-600" : "text-[#595959]"}`}>{value.length}/{max}</span>}
      </span>
      {hint && <span className="mb-1 block text-xs text-[#595959]">{hint}</span>}
      {multiline
        ? <textarea rows={3} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} className={ETSY_INPUT} />
        : <input value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} className={ETSY_INPUT} />}
    </label>
  );
}

function EtsyOptions({ cat, field: f, onChange }: { cat: ProductCategory; field: ListingAnalysisField; onChange: (p: Partial<ListingAnalysisField>) => void }) {
  const src = f.status === "existing" ? axisValuesOf(cat, f.sourceAxis) : [];
  const values = f.values ?? [];
  const rows = src.length || values.length;
  // Etsy sinirlari (API'de olculdu): varyasyon secenegi 255, kisisellestirme acilir listesi 20
  const max = f.role === "personalization" ? 20 : 255;
  // Varyasyon fiyati: fiyat eksenindeki her degerin tedarikci maliyeti (sizeOptions)
  const isPrice = !!f.sourceAxis && (cat.axisRoles ?? {})[f.sourceAxis] === "price";
  const costOf = (v: string | undefined) => (isPrice && v ? cat.sizeOptions.find((so) => so.size === v)?.cost : undefined);
  const setAt = (i: number, v: string) => {
    const next = Array.from({ length: Math.max(rows, values.length) }, (_, j) => values[j] ?? "");
    next[i] = v;
    onChange({ values: next });
  };
  return (
    <div className="mb-3">
      <p className="mb-1.5 text-sm font-semibold text-[#222]">Seçenekler</p>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="mb-1.5 flex items-center gap-2">
          {src.length > 0 && <span className="w-1/3 shrink-0 truncate text-[13px] text-[#595959]" title={src[i]}>{src[i]}</span>}
          {costOf(src[i]) != null && <span className="shrink-0 text-xs font-semibold text-[#222]" title="Tedarikçi maliyeti">${costOf(src[i])!.toFixed(2)}</span>}
          <input value={values[i] ?? ""} placeholder={src[i] ?? "Option"} onChange={(e) => setAt(i, e.target.value)} className={ETSY_INPUT} />
          <span className={`w-14 shrink-0 text-right text-xs ${(values[i] ?? "").length > max ? "text-red-600" : "text-[#595959]"}`}>{(values[i] ?? "").length}/{max}</span>
          {!src.length && (
            <button onClick={() => onChange({ values: values.filter((_, j) => j !== i) })} className="shrink-0 text-[#595959] hover:text-red-600" title="Seçeneği sil">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      ))}
      {!src.length && (
        <button onClick={() => onChange({ values: [...values, ""] })} className="text-sm font-bold text-[#222] underline">+ Seçenek ekle</button>
      )}
    </div>
  );
}

function EtsyFieldCard({ cat, field: f, index, picked, onTogglePicked, onChange, onRemove, libName, toLibrary, onToggleLibrary, libraryless }: {
  cat: ProductCategory; field: ListingAnalysisField; index: number; picked: boolean;
  onTogglePicked: () => void; onChange: (p: Partial<ListingAnalysisField>) => void; onRemove: () => void; libName?: string;
  toLibrary: boolean; onToggleLibrary: () => void;
  /** Bagli set urune ozel (kutuphanede degil) — kutuphaneye tasinabilir */
  libraryless?: boolean;
}) {
  const isVar = f.role === "variation";
  const priceAxis = !!f.sourceAxis && (cat.axisRoles as Record<string, string> | undefined)?.[f.sourceAxis] === "price";
  return (
    <div className={`mb-2.5 rounded-xl border p-3 ${picked ? "border-[#c7c7c7]" : "border-[#e1e3df] opacity-60"}`}>
      <div className="mb-2.5 flex flex-wrap items-center gap-2">
        <input type="checkbox" checked={picked} onChange={onTogglePicked} title="Listeye ekle (Seçeneklere uygula)" className="h-4 w-4 accent-[#222]" />
        <span className="text-[15px] font-bold text-[#222]">
          {isVar ? `Varyasyon ${index}` : `Soru ${index} · ${f.kind === "text" ? "Metin kutusu" : f.kind === "list" ? "Açılır liste" : "Dosya yükleme"}`}
        </span>
        <span className="text-[13px] text-[#595959]">({f.labelTr})</span>
        {priceAxis && <EtsyBadge tone="price">Fiyat bu varyasyona bağlı</EtsyBadge>}
        {f.status === "missing"
          ? <span title="Rexven'de bu ürünün varyant ekseni olarak gelmedi (yok ya da içe aktarımda sabitlendi). Listeye yeni seçenek olarak eklenecek."><EtsyBadge tone="warn">Listeye eklenecek</EtsyBadge></span>
          : <span title="Rexven'deki varyant ekseni — değerler Etsy etiketleriyle listeye girer."><EtsyBadge tone="ok">Rexven ekseni: {f.sourceAxis}</EtsyBadge></span>}
        {libName && <EtsyBadge tone="info">Kütüphane: {libName}</EtsyBadge>}
        <button onClick={onRemove} className="ml-auto text-[#595959] hover:text-red-600" title="Alanı çıkar"><Trash2 className="h-4 w-4" /></button>
      </div>
      <EtsyInput label={isVar ? "Varyasyon adı" : "Soru"} value={f.question ?? ""} max={45}
        placeholder={isVar ? "e.g. Length" : "e.g. Initials"} onChange={(v) => onChange({ question: v })} />
      {f.kind === "list" && <EtsyOptions cat={cat} field={f} onChange={onChange} />}
      {!isVar && f.kind !== "list" && (
        <EtsyInput label="Alıcıya talimat" value={f.instructions ?? ""} max={120} multiline
          hint="Etsy'de sorunun altında görünür. Altına örneği yazın: Ex: ve alıcının yazacağı biçim" onChange={(v) => onChange({ instructions: v })} />
      )}
      <div className="flex flex-wrap items-end gap-3">
        {f.kind === "text" && (
          <label className="block w-28">
            <span className="text-sm font-semibold text-[#222]">Karakter sınırı</span>
            <input type="number" min={1} max={1024} value={f.maxChars ?? ""} className={ETSY_INPUT}
              onChange={(e) => onChange({ maxChars: Number(e.target.value) || undefined })} />
          </label>
        )}
        <label className="mb-2 flex items-center gap-1.5 text-sm text-[#222]">
          <input type="checkbox" checked={!!f.required} onChange={(e) => onChange({ required: e.target.checked })} className="h-4 w-4 accent-[#222]" />Zorunlu
        </label>
        {/* Kutuphanede karsiligi yoksa uygula yeni set acar: varsayilan urune ozel */}
        {(!f.librarySetId || libraryless) && !(f.status === "existing" && f.sourceAxis) && (
          <label className="mb-2 flex items-center gap-1.5 text-sm text-[#222]" title="İşaretlemezsen set yalnızca bu ürüne ait olur, kütüphanede görünmez">
            <input type="checkbox" checked={toLibrary} onChange={onToggleLibrary} className="h-4 w-4 accent-[#222]" />Seçenek kütüphanesine ekle
          </label>
        )}
      </div>
      {f.status === "missing" && f.kind === "list" && (
        <button onClick={() => onChange({ role: isVar ? "personalization" : "variation" })} className="mt-2 text-[13px] text-[#222] underline">
          {isVar ? "Kişiselleştirme sorusuna taşı" : "Varyasyona taşı"}
        </button>
      )}
      {f.reasonTr && (
        <details className="mt-2 text-[13px] text-[#595959]">
          <summary className="cursor-pointer">AI gerekçesi</summary>
          <p className="mt-1">{f.reasonTr}{f.evidenceTr ? ` · ${f.evidenceTr}` : ""}</p>
        </details>
      )}
    </div>
  );
}

/** Etsy urun sayfasi gibi galeri: buyuk kapak + kucukler; chart'lar sonda (turuncu cerceve) */
function EtsyGallery({ photos, charts: chartsIn }: { photos: string[]; charts: string[] }) {
  // Onizlemede yalnizca ana gorsel + chart'lar
  const first = photos[0] ?? chartsIn[0];
  const charts = chartsIn.filter((c) => c !== first);
  const [main, setMain] = useState(0);
  if (!first) return null;
  return (
    <div className="mb-3">
      <img src={[first, ...charts][Math.min(main, charts.length)]} alt="" className="aspect-square w-full rounded-xl border border-[#e1e3df] object-cover" />
          {/* Chart gorselleri (ana gorselin altinda; tiklayinca buyuk) */}
          {charts.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {[first, ...charts].map((u, i) => (
                <button key={`${u}-${i}`} onClick={() => setMain(i)} title={i > 0 ? "Chart (fotoğrafların sonuna eklenir)" : "Ana görsel"}>
                  <img src={u} alt="" className={`h-12 w-12 rounded-md border-2 object-cover ${i === main ? "border-[#222]" : "border-[#e1e3df]"}`} />
                </button>
              ))}
            </div>
          )}
    </div>
  );
}

function EtsyBuyerPreview({ cat, fields, picked, photos, charts }: {
  cat: ProductCategory; fields: ListingAnalysisField[]; picked: Set<string>; photos: string[]; charts: string[];
}) {
  const roles = (cat.axisRoles ?? {}) as Record<string, string>;
  const chosen = fields.filter((f) => picked.has(f.key));
  const vars = chosen.filter((f) => f.role === "variation")
    .sort((x, y) => Number(roles[y.sourceAxis ?? ""] === "price") - Number(roles[x.sourceAxis ?? ""] === "price"));
  const selectCls = "w-full rounded-full border border-[#c7c7c7] bg-white px-4 py-2.5 text-sm text-[#222]";
  return (
    <div className="max-w-md">
      <EtsyGallery photos={photos} charts={charts} />
      <p className="mb-3 text-xl text-[#222]">{cat.name}</p>
      {vars.map((f) => (
        <label key={f.key} className="mb-3 block">
          <span className="mb-1 block text-sm text-[#222]">{f.question || f.labelTr}</span>
          <select className={selectCls}>
            <option>Select an option</option>
            {(f.values?.length ? f.values : axisValuesOf(cat, f.sourceAxis)).filter(Boolean).map((v, i) => <option key={i}>{v}</option>)}
          </select>
        </label>
      ))}
      {chosen.filter((f) => f.role === "personalization").map((f) => (
        <div key={f.key} className="mb-3">
          <span className="block text-sm text-[#222]">{f.question || f.labelTr}{f.required ? "" : " (optional)"}</span>
          {f.kind === "list" ? (
            <select className={`${selectCls} mt-1`}>
              <option>Select an option</option>
              {(f.values ?? []).filter(Boolean).map((v, i) => <option key={i}>{v}</option>)}
            </select>
          ) : f.kind === "upload" ? (
            <div className="mt-1 rounded-xl border-2 border-dashed border-[#c7c7c7] p-3 text-center text-[13px] text-[#595959]">Upload a file</div>
          ) : (
            <>
              {f.instructions && <p className="mb-1 mt-0.5 whitespace-pre-line text-[13px] text-[#595959]">{f.instructions}</p>}
              <textarea rows={2} placeholder={f.example ?? ""} className="w-full rounded-xl border border-[#c7c7c7] px-3 py-2 text-sm" />
              <p className="text-right text-xs text-[#595959]">0/{f.maxChars ?? 256}</p>
            </>
          )}
        </div>
      ))}
      <div className="mt-1 rounded-full bg-[#222] py-3 text-center text-[15px] font-bold text-white">Add to cart</div>
    </div>
  );
}

const IMAGE_ROLE_SHORT: Record<string, string> = {
  product: "Ürün", lifestyle: "Yaşam", detail: "Detay", size_chart: "Beden tablosu", font_chart: "Font chart",
  color_chart: "Renk chart", other_chart: "Chart", badge: "Rozet", other: "Diğer",
};

function EtsyListingView({
  cat, analysis, fields, images, setName, setChart, updateField, removeField,
  pickedFields, togglePicked, pickedCharts, toggleChart, libraryKeys, toggleLibrary, setInLibrary,
  charts, libraryCharts, onPickChart,
}: {
  /** Duzenlenebilir chart listesi (analysis.charts yerine) */
  charts: ListingAnalysisChart[];
  /** Kutuphanedeki chart gorselleri — secicide */
  libraryCharts: Array<{ id: string; name: string; url: string }>;
  onPickChart: (i: number, sel: { kind: "library"; id: string } | { kind: "supplier"; index: number }) => void;
  libraryKeys: Set<string>; toggleLibrary: (key: string) => void;
  /** Setin kutuphanede olup olmadigi (undefined: bilinmiyor) */
  setInLibrary: (id: string) => boolean | undefined;
  cat: ProductCategory; analysis: NonNullable<ProductCategory["listingAnalysis"]>; fields: ListingAnalysisField[];
  images: string[]; setName: (id?: string) => string | undefined;
  /** Kutuphane setinin chart gorseli (mutlak adres) */
  setChart: (id?: string) => string | undefined;
  updateField: (key: string, patch: Partial<ListingAnalysisField>) => void; removeField: (key: string) => void;
  pickedFields: Set<string>; togglePicked: (key: string) => void;
  pickedCharts: Set<number>; toggleChart: (i: number) => void;
}) {
  const vars = fields.filter((f) => f.role === "variation");
  const pers = fields.filter((f) => f.role === "personalization");
  const notNeeded = charts.filter((c) => c.status === "not_needed");
  const [pickIdx, setPickIdx] = useState<number | null>(null);
  // Alici onizlemesi: analizin "kullanma" dedikleri haric fotograflar; secili/bagli chart'lar sonda
  const usablePhotos = images.filter((_, i) => analysis.images.find((im) => im.index === i)?.usable !== false).slice(0, 10);
  const chartUrls = [
    ...charts.flatMap((c, i) => {
      if (!pickedCharts.has(i)) return [];
      if (c.status === "in_supplier_images") return images[c.imageIndexes?.[0] ?? -1] ? [images[c.imageIndexes![0]]] : [];
      if (c.status === "in_library") { const u = setChart(c.librarySetId); return u ? [u] : []; }
      return [];
    }),
    ...(cat.optionRefs ?? []).map((r) => setChart(r.optionSetId)).filter((u): u is string => !!u),
  ].filter((u, i, all) => all.indexOf(u) === i);
  const card = (f: ListingAnalysisField, i: number) => (
    <EtsyFieldCard key={f.key} cat={cat} field={f} index={i + 1} picked={pickedFields.has(f.key)}
      onTogglePicked={() => togglePicked(f.key)} onChange={(patch) => updateField(f.key, patch)}
      onRemove={() => removeField(f.key)} libName={setName(f.librarySetId)}
      toLibrary={libraryKeys.has(f.key)} onToggleLibrary={() => toggleLibrary(f.key)}
      libraryless={!!f.librarySetId && setInLibrary(f.librarySetId) === false} />
  );
  return (
    <div className="space-y-3 rounded-xl bg-[#f6f6f5] p-3 text-sm text-[#222]">
      <EtsySection title="Fotoğraflar" subtitle="İlk fotoğraf kapak olur; chart'lar fotoğrafların sonuna eklenir.">
        <div className="grid grid-cols-[repeat(auto-fill,minmax(88px,1fr))] gap-2">
          {images.map((u, i) => {
            const info = analysis.images.find((im) => im.index === i);
            return (
              <div key={u} className={`relative ${info && !info.usable ? "opacity-45" : ""}`} title={info?.noteTr}>
                <img src={u} alt="" className="aspect-square w-full rounded-lg border border-[#e1e3df] object-cover" />
                {i === 0 && <span className="absolute left-1 top-1 rounded-full bg-[#222] px-2 text-[11px] text-white">Kapak</span>}
                {info && <p className="mt-0.5 text-xs text-[#595959]">{IMAGE_ROLE_SHORT[info.role] ?? info.role}{info.usable ? "" : " · kullanma"}</p>}
              </div>
            );
          })}
          {charts.map((c, i) => {
            if (c.status === "not_needed") return null;
            const applicable = c.status === "in_library" || c.status === "in_supplier_images";
            const src = c.status === "in_supplier_images" ? images[c.imageIndexes?.[0] ?? -1]
              : c.status === "in_library" ? setChart(c.librarySetId) : undefined;
            return (
              <div key={`chart-${i}`} title={[c.purposeTr, c.noteTr].filter(Boolean).join(" · ")}>
                {src
                  ? <img src={src} alt="" className="aspect-square w-full rounded-lg border-2 border-[#222] object-cover" />
                  : (
                    <div className={`flex aspect-square items-center justify-center rounded-lg border-2 border-dashed p-1.5 text-center text-xs ${applicable ? "border-[#222] text-[#222]" : "border-amber-600 text-amber-700"}`}>
                      {applicable ? `Kütüphaneden: ${setName(c.librarySetId) ?? "chart"} (görsel yok)` : c.status === "needs_choice" ? "Seçilmeli — adaylardan seç" : "Hazırlanmalı"}
                    </div>
                  )}
                <label className="mt-0.5 flex items-start gap-1 text-xs text-[#222]">
                  <input type="checkbox" disabled={!applicable} checked={pickedCharts.has(i)} onChange={() => toggleChart(i)} className="mt-0.5 accent-[#222]" />
                  {c.titleTr}
                </label>
                {/* Tedarikci fotografindaki chart: uygula set acar — kutuphaneye de eklenebilir */}
                {c.status === "in_supplier_images" && (
                  <label className="mt-0.5 flex items-center gap-1 text-[11px] text-[#595959]" title="Uygulayınca chart seti seçenek kütüphanesine de eklenir">
                    <input type="checkbox" checked={libraryKeys.has(`chart:${i}`)} onChange={() => toggleLibrary(`chart:${i}`)} className="accent-[#222]" />
                    Kütüphaneye ekle
                  </label>
                )}
                {/* Birden cok aday chart olabilir (or. dogum cicegi): dogrusunu kullanici secer */}
                {c.status !== "needs_creation" && (
                  <button onClick={() => setPickIdx(pickIdx === i ? null : i)} className="mt-0.5 text-[12px] font-semibold text-[#222] underline">
                    {pickIdx === i ? "Kapat" : c.status === "needs_choice" ? "Seç" : "Değiştir"}
                  </button>
                )}
              </div>
            );
          })}
          {/* Urune zaten bagli setlerin chart'lari (analizde ayrica gecmeyenler) — eklentideki gibi */}
          {(cat.optionRefs ?? []).map((r) => {
            const u = setChart(r.optionSetId);
            if (!u || charts.some((c) => c.librarySetId === r.optionSetId)) return null;
            if (charts.some((c) => c.status === "in_supplier_images" && (c.imageIndexes ?? []).some((ix) => images[ix] === u))) return null;
            return (
              <div key={`ref-${r.optionSetId}`} title="Ürüne bağlı setin chart'ı — listede fotoğrafların sonuna eklenir">
                <img src={u} alt="" className="aspect-square w-full rounded-lg border-2 border-[#222] object-cover" />
                <p className="mt-0.5 text-xs text-[#222]">{setName(r.optionSetId) ?? "Set"} (bağlı)</p>
              </div>
            );
          })}
        </div>
        {pickIdx != null && charts[pickIdx] && (
          <div className="mt-3 rounded-xl border border-[#c7c7c7] bg-white p-3">
            <p className="mb-2 text-sm font-semibold text-[#222]">"{charts[pickIdx].titleTr}" için chart seç</p>
            {libraryCharts.length > 0 && <p className="mb-1 text-xs text-[#595959]">Kütüphaneden</p>}
            <div className="mb-3 grid grid-cols-[repeat(auto-fill,minmax(88px,1fr))] gap-2">
              {libraryCharts.map((lc) => {
                const on = charts[pickIdx].status === "in_library" && charts[pickIdx].librarySetId === lc.id;
                return (
                  <button key={lc.id} onClick={() => { onPickChart(pickIdx, { kind: "library", id: lc.id }); setPickIdx(null); }} title={lc.name}
                    className={`rounded-lg p-0.5 text-left ${on ? "ring-2 ring-[#222]" : "hover:ring-1 hover:ring-[#c7c7c7]"}`}>
                    <img src={lc.url} alt="" className="aspect-square w-full rounded-md border border-[#e1e3df] object-cover" />
                    <span className="block truncate text-[11px] text-[#222]">{lc.name}</span>
                  </button>
                );
              })}
            </div>
            <p className="mb-1 text-xs text-[#595959]">Ürünün tedarikçi fotoğraflarından</p>
            <div className="grid grid-cols-[repeat(auto-fill,minmax(88px,1fr))] gap-2">
              {images.map((u, ix) => {
                const on = charts[pickIdx].status === "in_supplier_images" && charts[pickIdx].imageIndexes?.[0] === ix;
                return (
                  <button key={`${u}-${ix}`} onClick={() => { onPickChart(pickIdx, { kind: "supplier", index: ix }); setPickIdx(null); }}
                    className={`rounded-lg p-0.5 ${on ? "ring-2 ring-[#222]" : "hover:ring-1 hover:ring-[#c7c7c7]"}`}>
                    <img src={u} alt="" className="aspect-square w-full rounded-md border border-[#e1e3df] object-cover" />
                  </button>
                );
              })}
            </div>
          </div>
        )}
        {notNeeded.length > 0 && (
          <p className="mt-2 text-[13px] text-[#595959]">Gerekmeyen: {notNeeded.map((c) => `${c.titleTr}${c.noteTr ? ` (${c.noteTr})` : ""}`).join(" · ")}</p>
        )}
      </EtsySection>

      <EtsySection title="Varyasyonlar" subtitle="Etsy en fazla 3 varyasyon kabul eder (fiyat ekseni dahil). Seçenek etiketleri en fazla 20 karakter.">
        {vars.length ? vars.map(card) : <p className="text-[#595959]">Varyasyon yok.</p>}
      </EtsySection>

      <EtsySection title="Kişiselleştirme" subtitle="Alıcının yazacağı / seçeceği bilgiler — Etsy en fazla 5 soru kabul eder.">
        {/* Her sorunun kendi talimati var; ayri "genel talimat" listeye aktarilmiyordu — gosterilmez */}
        {pers.length ? pers.map(card) : <p className="text-[#595959]">Bu üründe kişiselleştirme yok — listeye kişiselleştirme alanı eklenmeyecek.</p>}
      </EtsySection>

      <EtsySection title="Alıcı önizlemesi" subtitle="Ürün sayfasında alıcının göreceği seçim alanları">
        <EtsyBuyerPreview cat={cat} fields={fields} picked={pickedFields} photos={usablePhotos} charts={chartUrls} />
      </EtsySection>

      {analysis.supplierNotesTr.length > 0 && (
        <EtsySection title="Tedarikçi üretim notları">
          <ul className="list-disc pl-5 text-[13px]">{analysis.supplierNotesTr.map((n, i) => <li key={i}>{n}</li>)}</ul>
        </EtsySection>
      )}
    </div>
  );
}

/** Ogrenilmis kurallar: AI'in her analizde uydugu kurallar (elle de eklenir) */
export function LessonsManager() {
  const qc = useQueryClient();
  const { data } = useListingLessons();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [text, setText] = useState("");
  const [appliesTo, setAppliesTo] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const lessons = data?.data ?? [];

  async function mutate(path: string, method: string, body?: unknown) {
    setErr(null);
    try {
      await call(path, method, body);
      await qc.invalidateQueries({ queryKey: ["listing-lessons"] });
      return true;
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      return false;
    }
  }
  async function submit() {
    const ok = editing && editing !== "new"
      ? await mutate(`/api/listing-lessons/${editing}`, "PATCH", { text, appliesTo })
      : await mutate("/api/listing-lessons", "POST", { text, appliesTo });
    if (ok) { setEditing(null); setText(""); setAppliesTo(""); }
  }

  return (
    <div className="mt-3 border-t border-violet-100 pt-2">
      <button onClick={() => setOpen(!open)} className="flex items-center gap-1 text-xs font-semibold text-violet-800">
        {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        <BookOpen className="h-3.5 w-3.5" />Öğrenilen kurallar ({lessons.filter((l) => l.active).length})
      </button>
      {open && (
        <div className="mt-2 space-y-1.5">
          {lessons.map((l) => editing === l.id ? null : (
            <div key={l.id} className={`flex gap-2 rounded-lg border border-gray-200 bg-white p-2 text-xs ${l.active ? "" : "opacity-50"}`}>
              <input type="checkbox" checked={l.active} title="Etkin" className="mt-0.5"
                onChange={() => mutate(`/api/listing-lessons/${l.id}`, "PATCH", { active: !l.active })} />
              <div className="min-w-0 flex-1">
                {l.appliesTo && <p className="text-[10px] font-semibold uppercase text-violet-700">{l.appliesTo}</p>}
                <p className="text-gray-800">{l.text}</p>
                <p className="text-[10px] text-gray-400">
                  {l.source === "feedback" ? `Düzeltmeden öğrenildi${l.originTitle ? ` (${l.originTitle})` : ""}` : l.source === "seed" ? "Başlangıç kuralı" : "Elle eklendi"}
                  {l.useCount > 0 ? ` · ${l.useCount} analizde uygulandı` : ""}
                </p>
              </div>
              <button onClick={() => { setEditing(l.id); setText(l.text); setAppliesTo(l.appliesTo ?? ""); }}
                className="h-fit rounded p-0.5 text-gray-400 hover:text-gray-700" title="Düzenle"><Pencil className="h-3.5 w-3.5" /></button>
              <button onClick={() => { if (confirm("Kural silinsin mi?")) void mutate(`/api/listing-lessons/${l.id}`, "DELETE"); }}
                className="h-fit rounded p-0.5 text-gray-400 hover:text-red-600" title="Sil"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
          {editing ? (
            <div className="rounded-lg border border-violet-200 bg-white p-2">
              <input value={appliesTo} onChange={(e) => setAppliesTo(e.target.value)} placeholder="Kapsam (ör. Disk/Bar kazıma ürünleri)"
                className="mb-1.5 w-full rounded border border-gray-200 px-2 py-1 text-xs" />
              <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder="Kural"
                className="w-full rounded border border-gray-200 p-2 text-xs" />
              <div className="mt-1 flex justify-end gap-1.5">
                <button onClick={() => { setEditing(null); setText(""); setAppliesTo(""); }} className="rounded px-2 py-1 text-xs text-gray-600 hover:bg-gray-100">Vazgeç</button>
                <button onClick={submit} disabled={!text.trim()} className="rounded bg-violet-600 px-2 py-1 text-xs text-white disabled:opacity-50">Kaydet</button>
              </div>
            </div>
          ) : (
            <button onClick={() => setEditing("new")} className="flex items-center gap-1 text-xs text-violet-700 hover:underline">
              <Plus className="h-3.5 w-3.5" />Kural ekle
            </button>
          )}
          {err && <p className="text-xs text-red-600">{err}</p>}
          {(data?.systemRules ?? []).length > 0 && (
            <details className="text-xs">
              <summary className="cursor-pointer text-gray-500">Sistem kuralları ({data!.systemRules.length}) — kodda uygulanır, analiz bunlara da uyar</summary>
              <ol className="mt-1 list-decimal space-y-0.5 pl-5 text-gray-600">{data!.systemRules.map((r, i) => <li key={i}>{r}</li>)}</ol>
            </details>
          )}
        </div>
      )}
    </div>
  );
}
